/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {ExdsTheme} from "@exds/theme";
import {ExdsButton, ExdsLayout, ExdsLink, ExdsSpacing, ExdsText} from "@exds/core";
import {HELP_LINK} from "../../../../constants/constants";
import { TuxModal } from '@tuxedo/react-component-library';
import React, {useContext, useEffect, useState} from "react";
import {apiPost} from "../../../../service/service";
import Faye from 'faye';
import AppContext from "../../../../AppContext";
import Alert from "../../../Alert/Alert";
import QRCode from 'react-qr-code';
import {ExdsIconSuccess} from "@exds/icons";
import {addExitEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function TestDeviceModal(props) {
  const {isModalOpen, setIsModalOpen, channel, fayeUrl, nonce, qrCode, pageName, region} = props;
  const qrError = !region || region === '';
  const {sidContext, phoneNumberContext, hashContext, transactionIdContext, expirationTimeContext, hashStepsStatusContext, loadAA} = useContext(AppContext);
  const sid = sidContext.at(0);
  const phoneNumber = phoneNumberContext.at(0);
  const transactionId = transactionIdContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const [hashStepsStatus, setHashStepsStatus] = hashStepsStatusContext;
  const [hash, setHash] = hashContext;
  const [correlationId, setCorrelationId] = useState('');
  const [isAuthenticationError, setIsAuthenticationError] = useState(false);
  const [isAuthenticationSuccess, setIsAuthenticationSuccess] = useState(false);

  useEffect(() => {
    const testAuthentication = async () => {
      if(fayeUrl && fayeUrl !== '') {
        const faye = new Faye.Client(fayeUrl + '/faye');
        faye.subscribe("/messages/" + channel, async () => {
          apiPost('/api/smartLogOn/testDevice', {
            sid,
            phoneNumber,
            transactionId,
            qrCodeNonce: nonce,
            region,
            expirationTime,
            rsaSetup: hashStepsStatus?.rsaSetup,
            sloSetup: hashStepsStatus?.sloSetup,
            sloTest: hashStepsStatus?.sloTest,
            hash
          })
            .then((response) => {
              if (response.status === 200 && response?.data?.testResult === 'SUCCESS') {
                setHash(response.data.hash);
                setHashStepsStatus({
                  ...hashStepsStatus,
                  sloTest: true
                });
                setIsAuthenticationSuccess(true);
              } else {
                setCorrelationId(response.correlationId);
                setIsAuthenticationError(true);
              }
            })
            .catch((err) => {
              setCorrelationId(err.correlationId);
              setIsAuthenticationError(true);
            });
        });
      }
    };
    testAuthentication();
  }, [fayeUrl, region]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TuxModal
      className="modal-container test-device-modal"
      type='popup'
      title="Test Device"
      open={isModalOpen}
      onCloseModal={() => {
        setIsAuthenticationError(false);
        setIsAuthenticationSuccess(false);
        setIsModalOpen(false);
      }}
      body={
        <>
          {qrError &&
            <ExdsTheme><Alert
              errorMessage='QR Code is currently unavailable. Please try again later.'
              correlationId={correlationId}
              loadAA={loadAA}
              pageName={pageName}
              sid={sid}
            /></ExdsTheme>
          }
          {
            isAuthenticationSuccess && !qrError && <ExdsLayout
              className='test-device-success'
              flexDirection='column'
              gap='10px'
            >
              <ExdsIconSuccess
                aria-hidden
                color="success"
                size="extraLarge"
              />
              Smart Log On is working correctly
            </ExdsLayout>
          }
          {!isAuthenticationSuccess && !qrError && <ExdsTheme>
            <ExdsLayout justifyContent="space-around" alignItems="flex-start">
              <div className="modal-text">
                <ol>
                  <li>
                    <ExdsSpacing margin={{bottom: '1rem'}}>
                      <ExdsText>
                        Scan the QR code with your registered device, either with your phones native camera app or by
                        clicking 'QR Scan' within the Smart Log On app.
                      </ExdsText>
                    </ExdsSpacing>
                  </li>
                  <li>
                    <ExdsText>
                      Follow the prompts on your device and authenticate using your devices biometrics.
                    </ExdsText>
                  </li>
                </ol>
                <ExdsSpacing margin={{top: '1.5rem'}}>
                  <ExdsText>
                    Having trouble or not working?&nbsp;
                    <ExdsLink href={HELP_LINK} textDecoration={['underline']} className="fw-400" target="_blank" onClick={() => {
                      if(loadAA) {
                        addExitEvent(pageName, sid, HELP_LINK, 'Link', 'Get help', 'Test Device Modal');
                      }
                    }}>
                      Get help
                    </ExdsLink>
                  </ExdsText>
                </ExdsSpacing>
              </div>
              {qrCode && qrCode.length > 0 && <QRCode value={qrCode} aria-label="QR Code" role="img" className='qr-code'/>}
            </ExdsLayout>
            {isAuthenticationError && <Alert correlationId={correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/>}
          </ExdsTheme>
          }
        </>
      }
      footer={
        <ExdsTheme>
          <ExdsButton
            text="Close"
            variant="secondary"
            onClick={() => {
              setIsAuthenticationError(false);
              setIsAuthenticationSuccess(false);
              setIsModalOpen(false);
            }}
          />
        </ExdsTheme>
      }
    />
  );
}
